import React from 'react';
import { Typography, Input, Alert, Space, Tag, Divider } from 'antd';
import { VideoCameraOutlined } from '@ant-design/icons';
import { urlEmbedStreaming, urlExternaStreaming } from '../../../utils/streaming';

const { Text, Paragraph } = Typography;

/**
 * Transmisión en vivo del remate físico.
 *
 * Cargar la URL es lo único que hace falta: con URL, el micrositio muestra el
 * banner y habilita la página interna; sin URL, no. No hay un toggle aparte a
 * propósito — ver el comentario de `url_streaming` en el modelo Evento.
 */
const TransmisionTab = ({ config, onChange }) => {
  const url = config.url_streaming || '';
  const urlTrim = url.trim();
  const embed = urlEmbedStreaming(urlTrim);
  // Link válido pero no incrustable: el aviso sale igual, con botón afuera.
  const externa = !embed && !!urlExternaStreaming(urlTrim);

  // Amarillo y no rojo cuando el link sirve pero no se incrusta. En rojo, con
  // el aviso de abajo diciéndole que el banner sale igual, el productor cree
  // que rompió algo y borra un link que funciona.
  const statusInput = urlTrim && !embed ? (externa ? 'warning' : 'error') : '';

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Text strong>
          <VideoCameraOutlined /> &nbsp;Link de la transmisión en vivo
        </Text>
        <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 12, fontSize: 13 }}>
          Pegá el link del stream (YouTube, Vimeo, Facebook) o el código de
          incrustación que te da el proveedor. Mientras haya un link cargado, el
          micrositio muestra el aviso de <strong>remate en vivo</strong>. Si el
          link se puede incrustar, la transmisión se ve <strong>dentro de tu
          sitio</strong> y el comprador no se va a otra plataforma — que es lo
          que querés. Si no, le queda un botón para abrirla afuera.
        </Paragraph>

        <Input.TextArea
          value={url}
          onChange={(e) => onChange('url_streaming', e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          autoSize={{ minRows: 2, maxRows: 5 }}
          status={statusInput}
          allowClear
        />
      </div>

      {/* El estado del link, dicho en el momento en que se carga. Descubrir en
          plena transmisión que el reproductor quedó en blanco no es una opción. */}
      {urlTrim && !embed && (
        <Alert
          type={externa ? 'warning' : 'error'}
          showIcon
          message={
            externa
              ? 'Este link no se puede incrustar — el comprador va a salir de tu sitio'
              : 'Este link no es válido'
          }
          description={
            <span>
              {externa
                ? 'El aviso de remate en vivo se va a mostrar igual, pero en vez del reproductor el visitante va a ver un botón que lo lleva a otra plataforma. '
                : 'Revisá que esté bien escrito. '}
              Para que la transmisión se vea <strong>dentro de tu sitio</strong>,
              usá el link de un video o una transmisión puntual: el de un canal
              (ej: <code>youtube.com/@tucanal</code>) no apunta a una
              transmisión concreta. Si tu proveedor te da un código{' '}
              <code>&lt;iframe&gt;</code>, pegalo entero acá.
            </span>
          }
        />
      )}

      {embed && (
        <Alert
          type="success"
          showIcon
          message={<span>Link válido — se detectó <strong>{embed.proveedor}</strong></span>}
        />
      )}

      <div>
        <Text strong>Título de la página</Text>
        <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 8, fontSize: 13 }}>
          Opcional. Si lo dejás vacío decimos &ldquo;Transmisión en vivo&rdquo;.
        </Paragraph>
        <Input
          value={config.titulo_streaming || ''}
          onChange={(e) => onChange('titulo_streaming', e.target.value)}
          placeholder="Transmisión en vivo"
          allowClear
        />
      </div>

      {/* Preview real: el mismo iframe que va a ver el comprador. Es la única
          forma de saber ANTES del remate que el link anda — un link "válido"
          por su forma puede igual estar privado o no existir. */}
      {embed && (
        <>
          <Divider style={{ margin: '4px 0' }} />
          <div>
            <Text strong>Vista previa</Text>
            <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 8, fontSize: 12 }}>
              Así se va a ver en el micrositio. Si acá no reproduce, el video
              está privado o no existe — revisalo antes del remate.
            </Paragraph>
            <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
              <iframe
                key={embed.url}
                src={embed.url}
                title="Vista previa de la transmisión"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              />
            </div>
          </div>
        </>
      )}

      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        <Tag color="gold" style={{ marginRight: 8 }}>ℹ️ Tip</Tag>
        Para dar de baja el aviso cuando termina el remate, borrá el link y
        guardá.
      </Paragraph>
    </Space>
  );
};

export default TransmisionTab;
